import { WebsocketSubscriptionTarget, type GaiaEvent } from '@/api';
import { getWsClient, type WebSocketEventHandler } from '@/services/WebSocketService';
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { logger } from '@/utils/logger';

export const WEBSOCKET_DISABLED_KEY = 'websocket_disabled';

/**
 * Subscribe to one review's live events.
 *
 * This used to fan every `REVIEW_UPDATE` out to three stores, one of which
 * reloaded the org's entire review list to `.find()` a single uid and threw the
 * result away, while another reloaded every file review *and* every extraction
 * in parallel — `3 + N` requests per event on a review with N files, several
 * times a second while extractions were running.
 *
 * The event carries only a uid, so a refetch of *something* is unavoidable; what
 * is avoidable is refetching things the current view does not render. So this
 * only delivers the event, and the caller decides what to reload. Use
 * `useReviewLive` rather than calling this directly — it adds the coalescing
 * that stops a burst of events becoming a burst of requests.
 */
export function useReviewWebSocket(
  reviewId: string | undefined,
  onEvent: (event: GaiaEvent) => void
): void {
  const unsubscribe = ref<(() => void) | null>(null);

  const handler: WebSocketEventHandler = event => {
    try {
      onEvent(event);
    } catch (error) {
      logger.error('[WebSocket] Error handling event:', error, event);
    }
  };

  onMounted(() => {
    const websocketDisabled = localStorage.getItem(WEBSOCKET_DISABLED_KEY) === 'true';
    if (websocketDisabled) {
      logger.warn('[WebSocket] WebSocket is disabled via settings');
      return;
    }
    if (!reviewId) {
      logger.warn('[WebSocket] Missing review ID for subscription');
      return;
    }

    const wsClient = getWsClient();
    unsubscribe.value = wsClient.subscribe(WebsocketSubscriptionTarget.REVIEW, reviewId, handler);
  });

  onBeforeUnmount(() => {
    unsubscribe.value?.();
    unsubscribe.value = null;
  });
}
